import { useMemo, useState } from 'react';

import {
  IconAd,
  IconBan,
  IconBrandWhatsapp,
  IconBuildingBank,
  IconCheck,
  IconCreditCard,
  IconEye,
  IconEyeCheck,
  IconHeart,
  IconHeartPlus,
  IconLayoutGrid,
  IconListSearch,
  IconMail,
  IconMailForward,
  IconMailOpened,
  IconMessage,
  IconMessageChatbot,
  IconMouse,
  IconPhone,
  IconPlus,
  IconPlusMinus,
  IconQuestionMark,
  IconSearch,
  IconSettings,
  IconShare,
  IconShoppingCart,
  IconShoppingCartMinus,
  IconShoppingCartPlus,
  IconSitemap,
  IconStar,
  IconTag,
  IconTrendingDown,
  IconTrendingUp,
  IconTruck,
  IconUserCheck,
  IconUserMinus,
  IconUserPlus,
  IconWebhook,
  IconX,
} from '@tabler/icons-react';

import { Button, Combobox, Grid, ScrollArea, Text, useCombobox } from '@mantine/core';
import expandCustomActionRules from '../../constants/Rules/customActionRules';
import Spinner from '../Spinner';
import Rule from './Rule';

export const ruleIcons = {
  or: <IconSitemap size={16} />,
  currentShipmentStatus: <IconTruck size={16} />,
  orderStatusChanged: <IconTruck size={16} />,
  refund: <IconTrendingDown size={16} />,
  viewedProduct: <IconEye size={16} />,
  openedSurvey: <IconQuestionMark size={16} />,
  completedSurvey: <IconQuestionMark size={16} />,
  addedToWishlist: <IconStar size={16} />,
  removedFromWishlist: <IconX size={16} />,
  wishlistCreate: <IconStar size={16} />,
  viewedPage: <IconEye size={16} />,
  pageEngagement: <IconEyeCheck size={16} />,
  companyScope: <IconBuildingBank size={16} />,
  addedToCart: <IconShoppingCartPlus size={16} />,
  removedFromCart: <IconShoppingCartMinus size={16} />,
  cartCreate: <IconShoppingCartPlus size={16} />,
  cartUpdate: <IconShoppingCart size={16} />,
  checkoutInitiate: <IconShoppingCart size={16} />,
  addPaymentInfo: <IconCreditCard size={16} />,
  purchased: <IconCreditCard size={16} />,
  profileAttributes: <IconTag size={16} />,
  sentEmail: <IconMail size={16} />,
  deliveredEmail: <IconMail size={16} />,
  openedEmail: <IconMailOpened size={16} />,
  clickedEmail: <IconMailOpened size={16} />,
  bouncedEmail: <IconMailForward size={16} />,
  emailComplaint: <IconBan size={16} />,
  emailReschedule: <IconMail size={16} />,
  emailSkip: <IconBan size={16} />,
  profileRegistered: <IconUserCheck size={16} />,
  register: <IconUserCheck size={16} />,
  profileSubscribed: <IconUserPlus size={16} />,
  profileSubscriptionsChanged: <IconPlusMinus size={16} />,
  subscribeRequest: <IconUserPlus size={16} />,
  unsubscribed: <IconUserMinus size={16} />,
  interactedWithCampaign: <IconMail size={16} />,
  targetedByCampaign: <IconMail size={16} />,
  campaignEnter: <IconSitemap size={16} />,
  campaignSplitEnter: <IconShare size={16} />,
  search: <IconSearch size={16} />,
  autocompleteInteraction: <IconListSearch size={16} />,
  salesAgentProductClick: <IconMessageChatbot size={16} />,
  customAction: <IconSettings size={16} />,
  sentPush: <IconMail size={16} />,
  deliveredPush: <IconMail size={16} />,
  openedPush: <IconMailOpened size={16} />,
  clickedPush: <IconMailOpened size={16} />,
  bouncedPush: <IconMailForward size={16} />,
  pushNotificationSubscribe: <IconUserPlus size={16} />,
  sentTextMessage: <IconMessage size={16} />,
  clickedTextMessage: <IconMessage size={16} />,
  sentViberMessage: <IconPhone size={16} />,
  clickedViberMessage: <IconPhone size={16} />,
  clickedProductBlock: <IconLayoutGrid size={16} />,
  clickedBannerBlock: <IconAd size={16} />,
  recommenderClick: <IconLayoutGrid size={16} />,
  bannerClick: <IconAd size={16} />,
  viberMessageDelivery: <IconPhone size={16} />,
  viberMessageBounce: <IconPhone size={16} />,
  viberMessageOpen: <IconPhone size={16} />,
  viberMessageExpired: <IconPhone size={16} />,
  utmInteraction: <IconShare size={16} />,
  // profile attributes
  registered: <IconUserCheck size={16} />,
  subscribed: <IconUserPlus size={16} />,
  hasCart: <IconShoppingCart size={16} />,
  // catalog rules
  inStock: <IconCheck size={16} />,
  outOfStock: <IconX size={16} />,
  priceIncrease: <IconTrendingUp size={16} />,
  priceDecrease: <IconTrendingDown size={16} />,
  // segment rule
  belongsToSegment: <IconPlusMinus size={16} />,
  sentWhatsappMessage: <IconBrandWhatsapp size={16} />,
  clickedWhatsappMessage: <IconBrandWhatsapp size={16} />,
  whatsappMessageDelivery: <IconBrandWhatsapp size={16} />,
  whatsappMessageBounce: <IconBrandWhatsapp size={16} />,
  openedWhatsappMessage: <IconBrandWhatsapp size={16} />,
  whatsappMessageDeleted: <IconBrandWhatsapp size={16} />,
  webhookOpened: <IconMailOpened size={16} />,
  webhookClicked: <IconMouse size={16} />,
  webhookBounced: <IconX size={16} />,
  webhookSent: <IconWebhook size={16} />,
  webhookDelivered: <IconWebhook size={16} />,
  loyaltyGrant: <IconHeartPlus size={16} />,
  loyaltyPurchase: <IconHeart size={16} />,
  loyaltyCouponPurchase: <IconHeart size={16} />,
  merge: <IconSitemap size={16} />,
};

const Rules = ({
  emails,
  campaigns,
  webhooks,
  pushNotifications,
  eventTags,
  eventActions,
  rules,
  updateRules,
  textMessages,
  viberMessages,
  availableCustomFields,
  readOnly,
  pages,
  subscriptions,
  grouping = 'and',
  availableRules,
  availableFilters,
  flattenProductCustomFields = false,
  segments,
  whatsappMessages,
  invertible,
  surveys,
}) => {
  const [rulesState, setRulesState] = useState(rules.map((r) => rules.indexOf(r) === rules.length - 1));
  const [search, setSearch] = useState('');

  const addRule = (rule) => {
    // A fresh filter object per instance. The rule catalogues are module-level constants,
    // so resolving the filters in place made every instance of a rule - and every form
    // opened in the same session - share one filter object and one set of values.
    const ruleToAdd = {
      ...rule,
      filters: (rule.filters ?? []).map((filter) => ({
        ...filter,
        ...availableFilters[filter.key],
        values: [],
        ...rule.availableFilters[filter.key],
      })),
    };
    const updatedRules = [ruleToAdd, ...rules];
    updateRules(updatedRules);
    const updatedState = [true, ...rulesState];
    setRulesState(updatedState);
  };

  const updateRule = (index, rule) => {
    const updatedRules = [...rules];
    updatedRules[index] = rule;
    updateRules(updatedRules);
  };

  const removeRule = (index) => {
    const updatedRules = [...rules];
    updatedRules.splice(index, 1);
    const updatedState = [...rulesState];
    updatedState.splice(index, 1);

    updateRules(updatedRules);
    setRulesState(updatedState);
  };

  const toggle = (index) => {
    const updatedState = [...rulesState];
    updatedState[index] = !rulesState[index];
    setRulesState(updatedState);
  };

  // One ready-made "Performed action: <event>" rule per custom event the domain emits,
  // in place of making the user add an "Action name" filter to the generic rule.
  const selectableRules = useMemo(
    () => expandCustomActionRules(availableRules, eventActions),
    [availableRules, eventActions]
  );

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch('');
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput();
    },
  });

  if (!rules || !availableCustomFields || !eventActions || !subscriptions) {
    return <Spinner />;
  }

  const ruleElements = [];
  for (const rule of rules) {
    const index = rules.indexOf(rule);
    let divider;

    if (index > 0) {
      divider = <div>{grouping}</div>;
    }

    ruleElements.push(
      <div key={index} data-rule-name={rule.name}>
        {divider}
        <Rule
          filters={availableFilters}
          readOnly={readOnly}
          campaigns={campaigns}
          webhooks={webhooks}
          emails={emails}
          textMessages={textMessages}
          viberMessages={viberMessages}
          pushNotifications={pushNotifications}
          eventTags={eventTags}
          eventActions={eventActions}
          subscriptions={subscriptions}
          index={index}
          availableCustomFields={availableCustomFields}
          rules={rules}
          updateRules={updateRules}
          updateRule={(rule) => updateRule(index, rule)}
          removeRule={() => removeRule(index)}
          rule={rule}
          toggle={() => toggle(index)}
          open={rulesState[index]}
          icon={ruleIcons[rule.key]}
          pages={pages}
          availableRules={availableRules}
          availableFilters={availableFilters}
          flattenProductCustomFields={flattenProductCustomFields}
          segments={segments}
          whatsappMessages={whatsappMessages}
          invertible={invertible}
          surveys={surveys}
        />
      </div>
    );
  }

  const filteredRules = selectableRules
    .map((rule, index) => ({ rule, index }))
    .filter(({ rule }) => rule.name.toLowerCase().includes(search.toLowerCase().trim()));

  const options = filteredRules.map(({ rule, index }) => (
    <Combobox.Option value={index.toString()} key={`${rule.key}:${rule.name}`}>
      {ruleIcons[rule.key]} {rule.name}
    </Combobox.Option>
  ));

  const dropdownContent = (
    <div data-tour="segmentAddRuleBtn">
      <Combobox
        store={combobox}
        width={350}
        position="bottom-start"
        withArrow
        onOptionSubmit={(value) => {
          addRule(selectableRules[value]);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target withAriaAttributes={false}>
          <Button onClick={() => combobox.toggleDropdown()} leftSection={<IconPlus size={16} />}>
            Add Rule
          </Button>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Search
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search rules..."
          />
          <Combobox.Options>
            <ScrollArea.Autosize type="auto" mah={300}>
              {options.length > 0 ? options : <Combobox.Empty>Nothing found</Combobox.Empty>}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );

  return (
    <>
      {!readOnly && (
        <>
          <Grid>
            <Grid.Col span={12}>{dropdownContent}</Grid.Col>
          </Grid>
          <br />
        </>
      )}
      <Grid>
        <Grid.Col span={12}>
          {ruleElements.length > 0 ? (
            ruleElements
          ) : readOnly ? (
            <Text size="sm" c="dimmed">
              No rules.
            </Text>
          ) : null}
        </Grid.Col>
      </Grid>
    </>
  );
};

export default Rules;
